import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BarChart3, Package, Users, ShoppingCart, Plus, Edit, Trash2, ChevronDown, X, FileText, Receipt, Upload, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI, categoryAPI } from '../api';
import InvoiceModal from '../components/InvoiceModal';
import api from '../api';

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border rounded p-5 flex items-start gap-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={`w-10 h-10 rounded flex items-center justify-center ${color}`}><Icon size={20} /></div>
      <div><p className="text-xs text-gray-500 font-medium uppercase tracking-wider">{label}</p><p className="text-2xl font-black text-gray-900" style={{ fontFamily: "'Chivo', sans-serif" }}>{value}</p></div>
    </div>
  );
}

export default function AdminPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('products');
  const [stats, setStats] = useState({});
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', price: '', original_price: '', category: '', subcategory: '', brand: '', image_url: '', sku: '', in_stock: true, stock_quantity: '100', featured: false });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    fetchData();
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [s, p, o, c, inv] = await Promise.all([
        adminAPI.getStats(), adminAPI.getProducts({ limit: 100 }), adminAPI.getOrders({ limit: 100 }), categoryAPI.getCategories(),
        api.get('/admin/invoices')
      ]);
      setStats(s.data);
      setProducts(p.data.products);
      setOrders(o.data.orders);
      setCategories(c.data);
      setInvoices(inv.data.invoices || []);
    } catch {} finally { setLoading(false); }
  };

  const handleFormChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/admin/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      handleFormChange('image_url', `${baseUrl}${data.url}`);
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const openCreateForm = () => {
    setEditProduct(null);
    setForm({ name: '', description: '', price: '', original_price: '', category: '', subcategory: '', brand: '', image_url: '', sku: '', in_stock: true, stock_quantity: '100', featured: false });
    setShowForm(true);
  };

  const openEditForm = (product) => {
    setEditProduct(product);
    setForm({
      name: product.name, description: product.description || '', price: String(product.price),
      original_price: product.original_price ? String(product.original_price) : '', category: product.category,
      subcategory: product.subcategory || '', brand: product.brand || '', image_url: product.image_url || '',
      sku: product.sku || '', in_stock: product.in_stock, stock_quantity: String(product.stock_quantity || 0),
      featured: product.featured || false
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...form,
      price: parseFloat(form.price),
      original_price: form.original_price ? parseFloat(form.original_price) : null,
      stock_quantity: parseInt(form.stock_quantity || '0'),
    };
    try {
      if (editProduct) {
        await adminAPI.updateProduct(editProduct.id, data);
      } else {
        await adminAPI.createProduct(data);
      }
      setShowForm(false);
      fetchData();
    } catch {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await adminAPI.deleteProduct(id);
      fetchData();
    } catch {}
  };

  const handleStatusChange = async (orderId, status) => {
    try {
      await adminAPI.updateOrderStatus(orderId, status);
      fetchData();
    } catch {}
  };

  if (authLoading) return <div className="max-w-7xl mx-auto px-4 py-12 animate-pulse"><div className="h-8 bg-gray-200 rounded w-48 mb-6" /><div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-24 bg-gray-200 rounded" />)}</div></div>;
  if (!user || user.role !== 'admin') return null;
  if (loading) return <div className="max-w-7xl mx-auto px-4 py-12 animate-pulse"><div className="h-8 bg-gray-200 rounded w-48 mb-6" /><div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-24 bg-gray-200 rounded" />)}</div></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>Admin Dashboard</h1>
        <Link to="/" className="text-sm text-[#0F766E] hover:underline font-medium">Back to Store</Link>
      </div>

      {/* Quick Actions */}
      <div className="mb-6 flex gap-3 flex-wrap">
        <Link to="/admin/invoices" className="inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-3 px-6 rounded-lg transition-all active:scale-95 text-sm" data-testid="goto-invoices-page">
          <Receipt size={18} /> Invoice Center
        </Link>
        <Link to="/admin/customers" className="inline-flex items-center gap-2 bg-[#0F766E] hover:bg-[#0D5F59] text-white font-bold py-3 px-6 rounded-lg transition-all active:scale-95 text-sm" data-testid="goto-customers-page">
          <Users size={18} /> Customer Database
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Package} label="Products" value={stats.total_products || 0} color="bg-blue-100 text-blue-600" />
        <StatCard icon={ShoppingCart} label="Total Orders" value={stats.total_orders || 0} color="bg-green-100 text-green-600" />
        <StatCard icon={Users} label="Users" value={stats.total_users || 0} color="bg-purple-100 text-purple-600" />
        <StatCard icon={BarChart3} label="Pending Orders" value={stats.pending_orders || 0} color="bg-amber-100 text-amber-600" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded p-1 w-fit">
        {['products', 'orders', 'invoices'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-5 py-2 rounded text-sm font-semibold transition-colors flex items-center gap-1.5 ${tab === t ? 'bg-[#0F766E] text-white' : 'text-gray-600 hover:bg-gray-200'}`}
            data-testid={`admin-tab-${t}`}
          >{t === 'invoices' && <Receipt size={14} />}{t.charAt(0).toUpperCase() + t.slice(1)}</button>
        ))}
      </div>

      {/* Products Tab */}
      {tab === 'products' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-500">{products.length} products</p>
            <button onClick={openCreateForm} className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold text-sm py-2 px-4 rounded flex items-center gap-1.5 transition-all active:scale-95" data-testid="add-product-btn">
              <Plus size={16} /> Add Product
            </button>
          </div>
          <div className="bg-white border rounded overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="products-table">
                <thead><tr className="bg-gray-50 text-left text-xs font-bold uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Product</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Price</th><th className="px-4 py-3">Stock</th><th className="px-4 py-3">Actions</th>
                </tr></thead>
                <tbody className="divide-y">
                  {products.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50" data-testid={`product-row-${p.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <img src={p.image_url || 'https://placehold.co/40x40'} alt="" className="w-10 h-10 rounded object-cover" />
                          <div><p className="font-semibold text-gray-900 line-clamp-1">{p.name}</p><p className="text-xs text-gray-500">{p.brand} | {p.sku}</p></div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{p.category}</td>
                      <td className="px-4 py-3 font-bold text-[#0F766E]">${p.price?.toFixed(2)}</td>
                      <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded ${p.in_stock ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>{p.in_stock ? `${p.stock_quantity} in stock` : 'Out of stock'}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => openEditForm(p)} className="p-1.5 text-gray-400 hover:text-[#0F766E] rounded hover:bg-gray-100" data-testid={`edit-product-${p.id}`}><Edit size={16} /></button>
                          <button onClick={() => handleDelete(p.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded hover:bg-red-50" data-testid={`delete-product-${p.id}`}><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Orders Tab */}
      {tab === 'orders' && (
        <div className="bg-white border rounded overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="orders-table">
              <thead><tr className="bg-gray-50 text-left text-xs font-bold uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3">Order ID</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Items</th><th className="px-4 py-3">Total</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th>
              </tr></thead>
              <tbody className="divide-y">
                {orders.map(o => (
                  <tr key={o.id} className="hover:bg-gray-50" data-testid={`order-row-${o.id}`}>
                    <td className="px-4 py-3 font-mono text-gray-900">{o.id.slice(0, 8)}...</td>
                    <td className="px-4 py-3 text-gray-600">{o.user_email}</td>
                    <td className="px-4 py-3 text-gray-600">{o.items?.length || 0} items</td>
                    <td className="px-4 py-3 font-bold text-[#0F766E]">${o.total?.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <select value={o.status} onChange={e => handleStatusChange(o.id, e.target.value)}
                        className="text-xs border rounded px-2 py-1 font-semibold bg-white focus:ring-1 focus:ring-[#0F766E]"
                        data-testid={`order-status-${o.id}`}
                      >
                        {['pending', 'processing', 'shipped', 'delivered', 'cancelled'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{new Date(o.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {orders.length === 0 && (
                  <tr><td colSpan="6" className="px-4 py-12 text-center text-gray-500">No orders yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invoices Tab */}
      {tab === 'invoices' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-500">{invoices.length} invoices</p>
            <button onClick={() => setShowInvoiceModal(true)} className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold text-sm py-2 px-4 rounded flex items-center gap-1.5 transition-all active:scale-95" data-testid="create-invoice-btn">
              <FileText size={16} /> Create Invoice
            </button>
          </div>
          <div className="bg-white border rounded overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="invoices-table">
                <thead><tr className="bg-gray-50 text-left text-xs font-bold uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Invoice #</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Vehicle</th><th className="px-4 py-3">Items</th><th className="px-4 py-3">Total</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th>
                </tr></thead>
                <tbody className="divide-y">
                  {invoices.map(inv => (
                    <tr key={inv.id} className="hover:bg-gray-50" data-testid={`invoice-row-${inv.id}`}>
                      <td className="px-4 py-3 font-mono font-bold text-[#0F766E]">{inv.invoice_number}</td>
                      <td className="px-4 py-3"><div className="font-semibold text-gray-900">{inv.customer_name}</div>{inv.customer_phone && <div className="text-xs text-gray-500">{inv.customer_phone}</div>}</td>
                      <td className="px-4 py-3 text-gray-600">{inv.vehicle_info || '-'}</td>
                      <td className="px-4 py-3 text-gray-600">{inv.items?.length || 0} parts</td>
                      <td className="px-4 py-3 font-bold text-[#0F766E]">${inv.total?.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <select value={inv.status} onChange={async e => { try { await api.put(`/admin/invoices/${inv.id}/status`, { status: e.target.value }); fetchData(); } catch {} }}
                          className={`text-xs border rounded px-2 py-1 font-semibold ${inv.status === 'paid' ? 'bg-green-50 text-green-700' : inv.status === 'sent' ? 'bg-blue-50 text-blue-700' : inv.status === 'cancelled' ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-700'}`}
                          data-testid={`invoice-status-${inv.id}`}>
                          {['draft', 'sent', 'paid', 'cancelled'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{new Date(inv.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {invoices.length === 0 && <tr><td colSpan="7" className="px-4 py-12 text-center text-gray-400">No invoices yet. Create your first invoice!</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      <InvoiceModal isOpen={showInvoiceModal} onClose={() => setShowInvoiceModal(false)} onCreated={() => fetchData()} />

      {/* Product Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="product-form-modal">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: "'Chivo', sans-serif" }}>{editProduct ? 'Edit Product' : 'Add Product'}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600" data-testid="close-form-btn"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Name</label>
                  <input value={form.name} onChange={e => handleFormChange('name', e.target.value)} required
                    className="w-full border-2 border-gray-200 rounded px-3 py-2 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="form-name" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Description</label>
                  <textarea value={form.description} onChange={e => handleFormChange('description', e.target.value)} rows={2}
                    className="w-full border-2 border-gray-200 rounded px-3 py-2 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="form-description" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Price</label>
                  <input type="number" step="0.01" value={form.price} onChange={e => handleFormChange('price', e.target.value)} required
                    className="w-full border-2 border-gray-200 rounded px-3 py-2 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="form-price" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Original Price</label>
                  <input type="number" step="0.01" value={form.original_price} onChange={e => handleFormChange('original_price', e.target.value)}
                    className="w-full border-2 border-gray-200 rounded px-3 py-2 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="form-original-price" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Category</label>
                  <select value={form.category} onChange={e => handleFormChange('category', e.target.value)} required
                    className="w-full border-2 border-gray-200 rounded px-3 py-2 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="form-category">
                    <option value="">Select...</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Brand</label>
                  <input value={form.brand} onChange={e => handleFormChange('brand', e.target.value)}
                    className="w-full border-2 border-gray-200 rounded px-3 py-2 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="form-brand" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">SKU</label>
                  <input value={form.sku} onChange={e => handleFormChange('sku', e.target.value)}
                    className="w-full border-2 border-gray-200 rounded px-3 py-2 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="form-sku" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Stock Qty</label>
                  <input type="number" value={form.stock_quantity} onChange={e => handleFormChange('stock_quantity', e.target.value)}
                    className="w-full border-2 border-gray-200 rounded px-3 py-2 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="form-stock" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Product Image</label>
                  <div className="flex gap-3 items-start">
                    {/* Preview */}
                    <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden shrink-0 bg-gray-50">
                      {form.image_url ? (
                        <img src={form.image_url} alt="Preview" className="w-full h-full object-cover" />
                      ) : (
                        <ImageIcon size={28} className="text-gray-300" />
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      {/* Upload button */}
                      <label className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold cursor-pointer transition-all ${uploading ? 'bg-gray-100 text-gray-400' : 'bg-[#0F766E] hover:bg-[#0D5F59] text-white active:scale-95'}`} data-testid="form-upload-btn">
                        <Upload size={16} /> {uploading ? 'Uploading...' : 'Upload Photo'}
                        <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} className="hidden" data-testid="form-upload-input" />
                      </label>
                      <p className="text-[10px] text-gray-400">JPG, PNG, WebP. Max 5MB.</p>
                      {/* Or paste URL */}
                      <input value={form.image_url} onChange={e => handleFormChange('image_url', e.target.value)} placeholder="Or paste image URL..."
                        className="w-full border-2 border-gray-200 rounded px-3 py-2 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="form-image-url" />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.in_stock} onChange={e => handleFormChange('in_stock', e.target.checked)} className="rounded" data-testid="form-in-stock" /> In Stock</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={e => handleFormChange('featured', e.target.checked)} className="rounded" data-testid="form-featured" /> Featured</label>
                </div>
              </div>
              <div className="flex gap-3 pt-4 border-t">
                <button type="button" onClick={() => setShowForm(false)} className="px-6 py-2.5 border rounded text-sm font-medium text-gray-700 hover:bg-gray-50" data-testid="form-cancel-btn">Cancel</button>
                <button type="submit" className="flex-1 bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-2.5 rounded transition-all active:scale-95" data-testid="form-submit-btn">
                  {editProduct ? 'Update Product' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
